import _, { debounce } from 'lodash';
import { action, computed, makeObservable, observable, toJS } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import styled from 'styled-components';

import {
  Button,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  Typography,
} from 'antd';
import { CurrentUserStore, ShopStore, WorkOrderStore } from 'stores';

import TotalAmount from 'components/TotalAmount';
import WorkOrderLineItemIssues from '../Issues/WOLineItemIssues';
import WorkOrderLineItemLabors from '../Labors/WOLineItemLabors';
import WorkOrderLineItemMisc from '../Misc/WOLineItemMisc';
import WorkOrderLineItemParts from '../Parts/WOLineItemParts';

import { customFleets } from 'shared';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

const { Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { Panel } = Collapse;

class WorkOrderLineItemDetailsDesktop extends Component {
  static propTypes = {
    childRef: PropTypes.func,
    onChangeServiceTask: PropTypes.func,
    onChangeDescription: PropTypes.func,
    lineItem: PropTypes.object,
    shopUsers: PropTypes.array,
    shopParts: PropTypes.array,
    currentCarIssues: PropTypes.array,
    onAddNewPart: PropTypes.func.isRequired,
    onUpdateLaborItem: PropTypes.func.isRequired,
    onUpdateMiscItem: PropTypes.func.isRequired,
    onUpdatePartItem: PropTypes.func.isRequired,
    onUpdateServiceItem: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    onSelectSystemCode: PropTypes.func,
    onSelectAsmCode: PropTypes.func,
  };

  workOrderLinesDetailsRefs = {};
  lineItem = {};
  title = '';
  description = '';
  laborTotal = 0;
  miscTotal = 0;
  partsTotal = 0;
  labours = [];
  misc = [];
  parts = [];
  services = [];
  shouldDisableLaborCost = false;
  shouldDisableMiscCost = false;
  shouldDisablePartsCost = false;

  constructor(props) {
    super(props);
    makeObservable(this, {
      lineItem: observable,
      title: observable,
      description: observable,
      laborTotal: observable,
      miscTotal: observable,
      partsTotal: observable,
      labours: observable,
      misc: observable,
      parts: observable,
      services: observable,
      shouldDisableLaborCost: observable,
      shouldDisableMiscCost: observable,
      shouldDisablePartsCost: observable,
      lineSubtotal: computed,
      localSystemCode: computed,
      localAssemblyCode: computed,
      updateLineItem: action,
      updateTitle: action,
      updateDescription: action,
      updateLaborTotal: action,
      updateMiscTotal: action,
      updatePartsTotal: action,
      updateLabours: action,
      updateMisc: action,
      updateParts: action,
      updateServices: action,
    });
  }

  componentDidMount() {
    this.updateLineItem(this.props.lineItem);
    this.updateTitle(this.props.lineItem.title);
    this.updateDescription(this.props.lineItem.description);
    this.updateLaborTotal(this.props.lineItem.laborTotal);
    this.updateMiscTotal(this.props.lineItem.miscTotal);
    this.updatePartsTotal(this.props.lineItem.partsTotal);
    this.updateLabours(this.props.lineItem.labours || []);
    this.updateMisc(this.props.lineItem.misc || []);
    this.updateParts(this.props.lineItem.parts || []);
    this.updateServices(this.props.lineItem.services || []);
    this.disableOrEnableLaborCost();
    this.disableOrEnablePartsCost();
    this.fetchSystemCode('', true);
  }

  updateLineItem = (value) => {
    this.lineItem = value;
  };

  updateTitle = (value) => {
    this.title = value;
  };

  updateDescription = (value) => {
    this.description = value;
  };

  updateLaborTotal = (value) => {
    this.laborTotal = value;
  };

  updateMiscTotal = (value) => {
    this.miscTotal = value;
  };

  updatePartsTotal = (value) => {
    this.partsTotal = value;
  };

  updateLabours = (value) => {
    this.labours = value;
  };

  updateMisc = (value) => {
    this.misc = value;
  };

  updateParts = (value) => {
    this.parts = value;
  };

  updateServices = (value) => {
    this.services = value;
  };

  get lineSubtotal() {
    return (this.laborTotal || 0) + (this.partsTotal || 0);
  }

  get localSystemCode() {
    const vmrsSysCode = this.lineItem?.vmrs_sys_code;
    const vmrsSysText = this.lineItem?.vmrs_sys_text;
    if (vmrsSysCode && vmrsSysText) {
      return `${vmrsSysCode} - ${vmrsSysText}`;
    }
    return this.lineItem?.vmrs_sys_code_id || null;
  }

  get localAssemblyCode() {
    const vmrsAsmCode = this.lineItem?.vmrs_asm_code;
    const vmrsAsmText = this.lineItem?.vmrs_asm_text;
    if (vmrsAsmCode && vmrsAsmText) {
      return `${vmrsAsmCode} - ${vmrsAsmText}`;
    }
    return this.lineItem?.vmrs_asm_code_id || null;
  }

  state = {
    currentServiceTaskOnSearch: '',
    fetchingSystemCode: false,
    currentSystemCodeOptions: [],
    fetchingAssemblyCode: false,
    systemCodeAsms: [],
    currentAssemblyCodeOptions: [],
  };

  laboursRef = null;
  miscRef = null;
  partsRef = null;
  issuesRef = null;

  serviceTaskSuggestions = _.chain(WorkOrderStore.key32ServiceTasks)
    .map((description, i) => ({
      description,
      i,
    }))
    .sortBy('description')
    .value();

  get serviceTasksOptions() {
    return this.serviceTaskSuggestions.map((serviceTask, index) => (
      <Option key={index} value={serviceTask.i}>
        {serviceTask.description}
      </Option>
    ));
  }

  get vmrsSystemCodeOptions() {
    return this.state.currentSystemCodeOptions.map(
      (systemCodeOption, index) => (
        <Option key={index} value={systemCodeOption.id}>
          {`${systemCodeOption.sys_text} - ${systemCodeOption.text}`}
        </Option>
      )
    );
  }

  get vmrsAssemblyCodeOptions() {
    return this.state.currentAssemblyCodeOptions.map(
      (assemblyCodeOption, index) => (
        <Option key={index} value={assemblyCodeOption.id}>
          {`${assemblyCodeOption.asm_text} - ${assemblyCodeOption.text}`}
        </Option>
      )
    );
  }

  disableOrEnableLaborCost = () => {
    this.shouldDisableLaborCost = !!this.labours.length;
  };

  disableOrEnableMiscCost = () => {
    this.shouldDisableMiscCost = !!this.misc.length;
  };

  disableOrEnablePartsCost = () => {
    this.shouldDisablePartsCost = !!this.parts.length;
  };

  fetchSystemCode = async (value, searchAll = false) => {
    this.lastFetchSystemCodeId += 1;
    const fetchId = this.lastFetchSystemCodeId;
    this.setState({ fetchingSystemCode: true });
    if (!value && !searchAll) {
      this.setState({ fetchingSystemCode: false });
      return;
    }
    const { data } = await WorkOrderStore.searchVmrs({
      value,
      type: 'system_code',
    });
    if (fetchId !== this.lastFetchSystemCodeId) {
      // for fetch callback order
      return;
    }
    this.setState({
      currentSystemCodeOptions: data,
      fetchingSystemCode: false,
    });
  };

  handleChangeSystemCode = (vmrsKeyId) => {
    this.setState({ fetchingSystemCode: false }, () => {
      this.fetchAsmCodesForSysCode(vmrsKeyId);

      // console.log(vmrsKeyId);

      if (this.props.onSelectSystemCode) {
        this.props.onSelectSystemCode(vmrsKeyId);
      }
    });
  };

  fetchAsmCodesForSysCode = async (vmrsKeyId) => {
    this.lastFetchAssemblyCodeId += 1;
    const fetchId = this.lastFetchAssemblyCodeId;
    this.setState({ fetchingAssemblyCode: true });
    if (!vmrsKeyId) {
      this.setState({ fetchingAssemblyCode: false });
      return;
    }
    const { data } = await WorkOrderStore.getAsmCodesForSysCode(vmrsKeyId);
    if (fetchId !== this.lastFetchAssemblyCodeId) {
      // for fetch callback order
      return;
    }
    this.setState({
      currentAssemblyCodeOptions: data,
      systemCodeAsms: data,
      fetchingAssemblyCode: false,
    });
  };

  filterAssemblyCodes = (value) => {
    const { systemCodeAsms } = this.state;
    const filteredAsmCodes = systemCodeAsms.filter((asm) => {
      return asm.text.toLowerCase().includes(value.toLowerCase());
    });
    this.setState({ currentAssemblyCodeOptions: filteredAsmCodes });
  };

  getKey = (arr) => {
    const newKey = _.random(10000);
    if (arr.some((a) => a.key === newKey)) {
      return this.getKey(arr);
    }
    return newKey;
  };

  onChangeServiceTask = (event) => {
    const { value } = event.target;
    this.updateTitle(value);
    if (this.props.onChangeServiceTask) {
      this.props.onChangeServiceTask(event);
    }
    if (this.props.onChangeField) {
      this.props.onChangeField(value, 'title');
    }
  };

  onChangeDescription = (event) => {
    const { value } = event.target;
    this.updateDescription(value);
    if (this.props.onChangeDescription) {
      this.props.onChangeDescription(event);
    }
    if (this.props.onChangeField) {
      this.props.onChangeField(value, 'description');
    }
  };

  onAddLabor = () => {
    const currentUserSettings = CurrentUserStore.user.settings;
    const key = this.getKey(this.labours);
    this.labours.push({
      key,
      name: `Labor ${key}`,
      id_labour: '',
      hours: 0,
      cost: currentUserSettings?.laborRate || 0,
    });
    this.disableOrEnableLaborCost();
    this.onChangeField(this.labours, 'labours');
  };

  onAddMisc = () => {
    // const currentUserSettings = CurrentUserStore.user.settings;
    const key = this.getKey(this.misc);
    this.misc.push({
      key,
      name: `Misc ${key}`,
      id_misc: '',
      desc: '',
      cost: 0,
    });
    this.disableOrEnableMiscCost();
    this.onChangeField(this.misc, 'misc');
  };

  onAddExistingPart = (existingPart = {}) => {
    const key = this.getKey(this.parts);
    this.parts.push({
      key,
      name: `Part ${key}`,
      id_vehicle_part: '',
      cost: 0,
      amount: 0,
      markup: 0,
      ...existingPart,
    });
    this.disableOrEnablePartsCost();
    this.onChangeField(this.parts, 'parts');
    // update parts total
    this.onUpdatePartLineTotal();
  };

  onAddIssue = () => {
    const key = this.getKey(this.services);
    this.services.push({
      key,
      name: `Issue ${key}`,
      id_vehicle_service: '',
    });
    this.onChangeField(this.services, 'services');
  };

  onRemoveLabor = (event, itemKey) => {
    const index = this.labours.findIndex((l) => l.key === itemKey);
    this.labours.splice(index, 1);
    this.disableOrEnableLaborCost();
    this.onChangeField(toJS(this.labours), 'labours');
  };

  onRemoveMisc = (event, itemKey) => {
    const index = this.misc.findIndex((l) => l.key === itemKey);
    this.misc.splice(index, 1);
    this.disableOrEnableMiscCost();
    this.onChangeField(toJS(this.misc), 'misc');
  };

  onRemovePart = (event, itemKey) => {
    const index = this.parts.findIndex((l) => l.key === itemKey);
    this.parts.splice(index, 1);
    this.disableOrEnablePartsCost();
    this.onChangeField(toJS(this.parts), 'parts');
  };

  onRemoveIssue = (event, itemKey) => {
    const index = this.services.findIndex((l) => l.key === itemKey);
    this.services.splice(index, 1);
    this.onChangeField(toJS(this.services), 'services');
  };

  onAddItemClick = (itemRef) => {
    switch (itemRef) {
      case 'Labor':
        return this.onAddLabor();
      case 'Misc':
        return this.onAddMisc();
      case 'Part':
        return this.onAddExistingPart();
      case 'Issue':
        return this.onAddIssue();
      default:
        return;
    }
  };

  onChangeField = (newVal, fieldName) => {
    this[fieldName] = newVal;

    if (this.props.onChangeField) {
      this.props.onChangeField(newVal, fieldName);
    }
  };

  onUpdateLaborLineTotal = () => {
    this.updateLaborTotal(_.sumBy(this.labours, (l) => l.hours * l.cost));

    if (this.props.onChangeField) {
      this.props.onChangeField(this.laborTotal, 'laborTotal');
    }
  };

  onUpdateMiscLineTotal = () => {
    this.updateMiscTotal(_.sumBy(this.misc, (l) => l.cost));

    if (this.props.onChangeField) {
      this.props.onChangeField(this.miscTotal, 'miscTotal');
    }
  };

  onUpdatePartLineTotal = () => {
    this.updatePartsTotal(
      _.sumBy(this.parts, (part) => {
        const cost = part.cost || 0;
        const markup = part.markup || 0;
        const amount = part.amount || 0;

        return (cost + (cost * markup) / 100) * amount;
      })
    );

    if (this.props.onChangeField) {
      this.props.onChangeField(this.partsTotal, 'partsTotal');
    }
  };

  onSelectServiceTask = (value) => {
    const suggestion = this.serviceTaskSuggestions.find((s) => s.i === value);
    this.updateTitle(suggestion.description);
    if (this.props.onChangeServiceTask) {
      this.props.onChangeServiceTask(null, suggestion.description);
    }
    if (this.props.onChangeField) {
      this.props.onChangeField(suggestion.description, 'title');
    }
  };

  onSearchServiceTask = (value) => {
    this.setState({ currentServiceTaskOnSearch: value });
  };

  onClickAddServiceTask = () => {
    const key = this.serviceTaskSuggestions.length + 1;
    this.serviceTaskSuggestions.push({
      i: key,
      description: this.state.currentServiceTaskOnSearch,
    });
    this.onSelectServiceTask(key);
  };

  render() {
    return (
      <Form>
        <Row gutter={[8, 8]}>
          <Col xs={24} sm={12}>
            <Text strong>VMRs System Code:</Text>
            <Select
              value={this.localSystemCode}
              placeholder="Search"
              notFoundContent={
                this.state.fetchingSystemCode ? <Spin size="small" /> : null
              }
              filterOption={false}
              onSearch={this.fetchSystemCode}
              style={{ width: '100%' }}
              onChange={this.handleChangeSystemCode}
              showSearch
              disabled={this.props.isDisabled}
              popupMatchSelectWidth={false}
            >
              {this.vmrsSystemCodeOptions}
            </Select>
          </Col>
          <Col xs={24} sm={12}>
            <Text strong>VMRs Assembly Code:</Text>
            <Select
              value={this.localAssemblyCode}
              onChange={(value) => {
                if (this.props.onSelectAsmCode) {
                  this.props.onSelectAsmCode(value);
                }
              }}
              placeholder="Search"
              style={{ width: '100%' }}
              notFoundContent={
                this.state.fetchingAssemblyCode ? <Spin size="small" /> : null
              }
              filterOption={false}
              onSearch={this.filterAssemblyCodes}
              showSearch
              disabled={this.props.isDisabled}
              popupMatchSelectWidth={false}
            >
              {this.vmrsAssemblyCodeOptions}
            </Select>
          </Col>
          <Col span={4}>
            <Text strong>Labor Cost:</Text>
            <TotalAmount amount={this.laborTotal} />
          </Col>
          {customFleets.ufpFleet.includes(
            ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
          ) && (
            <Col span={4}>
              <Text strong>Misc Cost:</Text>
              <InputNumber
                disabled={this.shouldDisableMiscCost || this.props.isDisabled}
                defaultValue={0}
                style={{ width: '100%' }}
                parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                formatter={(value) =>
                  `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                }
                value={this.miscTotal}
                onChange={(newVal) => this.onChangeField(newVal, 'miscTotal')}
              />
            </Col>
          )}
          <Col span={4}>
            <Text strong>Parts Cost:</Text>
            <TotalAmount amount={this.partsTotal} />
          </Col>
          <Col span={4}>
            <Text strong>Subtotal:</Text>
            <TotalAmount amount={this.lineSubtotal} />
          </Col>
          <Col span={24}>
            <Text strong>Description:</Text>
            <TextArea
              rows={4}
              placeholder="Add Description"
              value={this.description}
              onChange={this.onChangeDescription}
              disabled={this.props.isDisabled}
            />
          </Col>
          <Col span={24}>
            <Button
              onClick={() => this.onAddLabor()}
              icon={<PlusOutlined />}
              style={{ marginRight: '10px' }}
            >
              Add labor
            </Button>
            {customFleets.ufpFleet.includes(
              ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
            ) && (
              <Button
                onClick={() => this.onAddMisc()}
                icon={<PlusOutlined />}
                style={{ marginRight: '10px' }}
              >
                Add Misc Item
              </Button>
            )}
            <Button
              onClick={() => this.onAddExistingPart()}
              icon={<PlusOutlined />}
              style={{ marginRight: '10px' }}
            >
              Add Existing Part
            </Button>
            <Button
              onClick={() => {
                if (this.props.onAddNewPart) {
                  this.props.onAddNewPart();
                }
              }}
              icon={<PlusOutlined />}
              style={{ marginRight: '10px' }}
            >
              Add New Part
            </Button>
            <Button onClick={() => this.onAddIssue()} icon={<PlusOutlined />}>
              Link Existing Issues
            </Button>
          </Col>
          <Col span={24}>
            <WorkOrderLineItemLabors
              title="Labors (Technician)"
              description="Labor"
              items={this.labours}
              shopUsers={this.props.shopUsers}
              childRef={(ref) => (this.laborsRef = ref)}
              onAddItem={() => this.onAddItemClick('Labor')}
              onRemoveItem={this.onRemoveLabor}
              onUpdateLineTotal={this.onUpdateLaborLineTotal}
              onUpdateLocalItem={this.props.onUpdateLaborItem}
              isDisabled={this.props.isDisabled}
            />
          </Col>
          <Col span={24}>
            <WorkOrderLineItemMisc
              title="Miscellaneous"
              description="Misc"
              items={this.misc}
              shopUsers={this.props.shopUsers}
              childRef={(ref) => (this.miscRef = ref)}
              onAddItem={() => this.onAddItemClick('Misc')}
              onRemoveItem={this.onRemoveMisc}
              onUpdateLineTotal={this.onUpdateMiscLineTotal}
              onUpdateLocalItem={this.props.onUpdateMiscItem}
              isDisabled={this.props.isDisabled}
            />
          </Col>
          <Col span={24}>
            <WorkOrderLineItemParts
              title="Parts"
              description="Parts"
              items={this.parts}
              shopParts={this.props.shopParts}
              childRef={(ref) => (this.partsRef = ref)}
              onAddItem={() => this.onAddItemClick('Part')}
              onRemoveItem={this.onRemovePart}
              onUpdateLineTotal={this.onUpdatePartLineTotal}
              onAddNewPart={this.props.onAddNewPart}
              onUpdateLocalItem={this.props.onUpdatePartItem}
              isDisabled={this.props.isDisabled}
            />
          </Col>
          {!this.props.isDisabled && (
            <Col span={24}>
              <WorkOrderLineItemIssues
                title="Link issues"
                description="link issues"
                items={this.services}
                currentCarIssues={this.props.currentCarIssues}
                childRef={(ref) => (this.issuesRef = ref)}
                onAddItem={() => this.onAddItemClick('Issue')}
                onRemoveItem={this.onRemoveIssue}
                onUpdateLocalItem={this.props.onUpdateServiceItem}
              />
            </Col>
          )}
        </Row>
      </Form>
    );
  }
}

export default observer(WorkOrderLineItemDetailsDesktop);
